import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const images = [
  "https://i.postimg.cc/7hdy8vrM/KIM-1.jpg",
  "https://i.postimg.cc/0jFqRgqg/KIM-10.jpg",
  "https://i.postimg.cc/5yTMdcMd/KIM-11.jpg",
  "https://i.postimg.cc/fyphn4hN/KIM-12.jpg",
  "https://i.postimg.cc/6qgNJsNN/KIM-13.jpg",
  "https://i.postimg.cc/7hdy8vyw/KIM-14.jpg",
  "https://i.postimg.cc/rs3LX6Lq/KIM-15.jpg",
  "https://i.postimg.cc/3NVH5MHJ/KIM-16.jpg",
  "https://i.postimg.cc/HWhm7M8c/KIM-17.jpg",
  "https://i.postimg.cc/hv6qWFqt/KIM-18.jpg",
  "https://i.postimg.cc/T1FXM8vC/KIM-2.jpg",
  "https://i.postimg.cc/HnRCDG1z/KIM-3.jpg",
  "https://i.postimg.cc/zv61ZmYx/KIM-4.jpg",
  "https://i.postimg.cc/Zn2htkzf/KIM-5.jpg",
  "https://i.postimg.cc/KjwySXhV/KIM-6.jpg",
  "https://i.postimg.cc/brWjcXf5/KIM-7.jpg",
  "https://i.postimg.cc/Zn2htkzX/KIM-8.jpg",
  "https://i.postimg.cc/7hdy8vrp/KIM-9.jpg"
];

const Gallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const nextSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  useEffect(() => {
    if (isLightboxOpen) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isLightboxOpen]);

  // Prevent scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isLightboxOpen]);

  return (
    <section id="gallery" className="py-20 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Our Clinic</h2>
          <p className="text-gray-600">Click any image to view in full screen</p>
        </div>

        <div className="relative max-w-5xl mx-auto h-[400px] md:h-[600px] rounded-2xl overflow-hidden shadow-2xl bg-white group cursor-zoom-in">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentIndex}
              src={images[currentIndex]}
              alt={`Clinic view ${currentIndex + 1}`}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 w-full h-full object-cover"
              onClick={() => setIsLightboxOpen(true)}
            />
          </AnimatePresence>

          {/* Overlay info */}
          <div className="absolute top-4 left-4 bg-black/30 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-medium pointer-events-none">
            {currentIndex + 1} / {images.length}
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"></div>
          
          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/90 backdrop-blur-md p-3 rounded-full text-white hover:text-gray-900 transition-all hover:scale-110 z-10"
          >
            <SafeIcon icon={FiIcons.FiChevronLeft} className="text-2xl" />
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/90 backdrop-blur-md p-3 rounded-full text-white hover:text-gray-900 transition-all hover:scale-110 z-10"
          >
            <SafeIcon icon={FiIcons.FiChevronRight} className="text-2xl" />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Portal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
            onClick={() => setIsLightboxOpen(false)}
          >
            <motion.button
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-6 right-6 text-white text-3xl p-2 hover:bg-white/10 rounded-full transition-colors z-[110]"
              onClick={() => setIsLightboxOpen(false)}
            >
              <SafeIcon icon={FiIcons.FiX} />
            </motion.button>

            {/* Navigation in Lightbox */}
            <button 
              onClick={prevSlide}
              className="absolute left-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-white text-4xl p-4 transition-colors hidden md:block"
            >
              <SafeIcon icon={FiIcons.FiChevronLeft} />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-white text-4xl p-4 transition-colors hidden md:block"
            >
              <SafeIcon icon={FiIcons.FiChevronRight} />
            </button>

            <motion.div
              layoutId={`img-${currentIndex}`}
              className="relative w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[currentIndex]}
                alt="Full clinic view"
                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
              />
              
              <div className="absolute bottom-0 left-0 right-0 text-center text-white/60 text-sm pb-4">
                Image {currentIndex + 1} of {images.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;